import { baseApi } from './baseApi';
import type { UserProfile, ImageUploadResponse, FollowListItem } from '../types';

// Page size for the follow lists. Matches the backend's MAX_FOLLOW_LIMIT so a
// full page means "there may be more" (hasNextPage = page.length === size).
export const FOLLOW_PAGE_SIZE = 50;

type InfiniteFollowData = { pages: FollowListItem[][] };

// A follow/unfollow touches TWO users' cached data: the target (their followers
// list + follower count) and the acting/authenticated user (their following list
// + following count). The following list is tagged by its owner's id, so we must
// invalidate the current user's id too — otherwise the actor's own following list
// stays stale after they follow/unfollow someone from another profile.
const followInvalidation = (
  _result: unknown,
  _error: unknown,
  { userId }: { userId: string },
): { type: 'User'; id: string }[] => {
  const tags: { type: 'User'; id: string }[] = [{ type: 'User', id: userId }];
  const currentUserId = localStorage.getItem('userId');
  if (currentUserId && currentUserId !== userId) {
    tags.push({ type: 'User', id: currentUserId });
  }
  return tags;
};

// Tag a follow list by its owner's id AND by every listed member's id (across
// all loaded pages), so cache invalidation of any of those User tags
// (follow/unfollow, or a member's avatar change) refreshes the list.
const followListTags = (
  result: InfiniteFollowData | undefined,
  _error: unknown,
  userId: string,
): { type: 'User'; id: string }[] => [
  { type: 'User', id: userId },
  ...(result?.pages ?? []).flat().map((u) => ({ type: 'User' as const, id: u._id })),
];

// Build the next-page cursor from the last row of a page, or undefined when the
// page came back short (no more rows). Keyset cursor = `${followedAt}_${relId}`.
const nextFollowCursor = (lastPage: FollowListItem[]): string | undefined => {
  if (!lastPage || lastPage.length < FOLLOW_PAGE_SIZE) return undefined;
  const last = lastPage[lastPage.length - 1];
  return `${last.followedAt}_${last.relId}`;
};

const followListUrl = (direction: 'followers' | 'following') =>
  ({ queryArg: userId, pageParam }: { queryArg: string; pageParam: string | undefined }) => {
    const params = new URLSearchParams({ limit: String(FOLLOW_PAGE_SIZE) });
    if (pageParam) params.set('cursor', pageParam);
    return `/users/${userId}/${direction}?${params.toString()}`;
  };

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query<UserProfile, string>({
      query: (userId) => `/users/${userId}`,
      extraOptions: { allowAnonymous: true }, // public read (isAuthGet)
      providesTags: (_result, _error, userId) => [{ type: 'User', id: userId }],
    }),

    // Users who follow this profile. Cursor-paginated (infinite scroll / load
    // more) so large profiles don't load the whole list — the backend caps each
    // page at FOLLOW_PAGE_SIZE. Tagged by the profile's User id (so a
    // follow/unfollow refreshes the list) AND by each listed member's id across
    // all loaded pages, so a member updating their avatar (uploadUserImage
    // invalidates their User tag) refetches instead of showing a stale image.
    getFollowers: builder.infiniteQuery<FollowListItem[], string, string | undefined>({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: nextFollowCursor,
      },
      query: followListUrl('followers'),
      providesTags: followListTags,
    }),

    // Users this profile follows.
    getFollowing: builder.infiniteQuery<FollowListItem[], string, string | undefined>({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: nextFollowCursor,
      },
      query: followListUrl('following'),
      providesTags: followListTags,
    }),

    uploadUserImage: builder.mutation<ImageUploadResponse, FormData>({
      query: (body) => ({
        url: '/users/change-profile-image',
        method: 'POST',
        body,
        formData: true,
      }),
      invalidatesTags: () => {
        const userId = localStorage.getItem('userId');
        return [
          { type: 'User', id: userId || undefined },
          'Questions',
          'Replies',
        ];
      },
    }),

    followUser: builder.mutation<void, { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/follow/${userId}`,
        method: 'POST',
      }),
      invalidatesTags: followInvalidation,
    }),

    unfollowUser: builder.mutation<void, { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/unfollow/${userId}`,
        method: 'POST',
      }),
      invalidatesTags: followInvalidation,
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetFollowersInfiniteQuery,
  useGetFollowingInfiniteQuery,
  useUploadUserImageMutation,
  useFollowUserMutation,
  useUnfollowUserMutation,
} = usersApi;
